if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;

        this.footerContent = this.querySelector("[data-quick-view-footer]");

        this.preventScrollBody();
      }

      hide(preventFocus = false) {
        this.abortPendingRequest();

        this.openedBy =
          this.openedBy
            ?.closest(".product-card")
            ?.querySelector(".product-card__name > a") || this.openedBy;
        if (preventFocus) this.openedBy = null;

        super.hide();

        this.addEventListener(
          "transitionend",
          () => {
            if (!this.hasAttribute("open")) {
              // Destroy Swipers before wiping their DOM, else each open/close orphans them.
              this.destroySliders();
              this.modalContent.innerHTML = "";
              if (this.footerContent) this.footerContent.innerHTML = "";
            }
          },
          { once: true }
        );

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        // Every click starts its own fetch, so a swatch tapped three times in a
        // row used to render three times into the same modal — each response
        // appending another buy-buttons block to the footer. Only the newest
        // request survives; the earlier ones are aborted.
        this.abortPendingRequest();
        const controller = new AbortController();
        this.pendingRequest = controller;
        this.pendingOpener = opener;

        this.toggleOpenerLoading(opener, true);

        fetch(opener.dataset.productUrl, { signal: controller.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML();
            this.initSlider();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);

            // styles for forced disable body scroll on iOS
            if (typeof window.lockScrollBodyOnIos === "function") {
              this.openScrollY = window.scrollY;
              window.lockScrollBodyOnIos(this.openScrollY);
            }
          })
          .catch((error) => {
            if (error?.name !== "AbortError") console.error(error);
          })
          .finally(() => {
            if (this.pendingRequest === controller) {
              this.pendingRequest = null;
              this.pendingOpener = null;
            }
            // A superseded request must not clear the spinner of the opener the
            // newer one is still loading.
            if (this.pendingOpener !== opener) {
              this.toggleOpenerLoading(opener, false);
            }
          });
      }

      abortPendingRequest() {
        this.pendingRequest?.abort();
        this.pendingRequest = null;
        this.pendingOpener = null;
      }

      toggleOpenerLoading(opener, isLoading) {
        opener.classList.toggle("loading", isLoading);
        if (isLoading) {
          opener.setAttribute("aria-disabled", true);
        } else {
          opener.removeAttribute("aria-disabled");
        }

        const btnSpinner = opener.querySelector(".button-loader");
        if (btnSpinner) btnSpinner.classList.toggle("hidden", !isLoading);
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
      }

      removeDOMElements(mainProductElement) {
        const selectors = [
          // Remove blocks from _product-blocks-under-gallery.liquid:
          ".product__gallery-blocks",
          // Remove blocks without specified data-attribute:
          ".product__info-container > :not([data-show-inside-quick-view='true'])",
          // Remove other blocks and elements:
          ".linked-products",
          ".product-popup",
          ".product-sticky-add-bar",
          ".pickup-availability",
          ".back-in-stock-alert",
          ".back-in-stock-alert-drawer",
          "[data-back-in-stock-alert-script]",
          ".product-form__checkout",
          "product-modal",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          ".custom-liquid",
          ".complementary-products",
        ];
        const removingElements = mainProductElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());

        const onlyOneBlockSelectors = [
          ".product-parameters",
          ".product__vendor",
          ".product__price",
          ".product__title",
          ".product__description",
          ".product__inventory",
          ".product__buy-buttons",
        ];

        onlyOneBlockSelectors.forEach((selector) => {
          const items = mainProductElement.querySelectorAll(selector);
          if (items.length > 1) {
            items.forEach((el, index) => {
              if (index > 0) el.remove();
            });
          }
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );

        this.productElement
          .querySelectorAll("variant-picker")
          .forEach((variantPicker) => {
            variantPicker.dataset.originalSection = sectionId;
          });
      }

      setModalHTML() {
        this.modalContent.innerHTML = this.productElement.innerHTML;
        // reorderElements() appends into the footer, so it has to start empty.
        if (this.footerContent) this.footerContent.innerHTML = "";

        this.reinjectScriptTags();
        this.createFullDetailsButton();
        this.reorderElements();
        this.preventVariantURLSwitching();
        this.clearMediaClasses();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      createFullDetailsButton() {
        const fullDetailsBtnTemplate = this.querySelector(
          "[data-full-details-button]"
        );
        if (!fullDetailsBtnTemplate) return;

        // Falls back to the end of the info container when the buy-buttons block is absent.
        const target =
          this.modalContent.querySelector(".product-form__buttons-row") ||
          this.modalContent.querySelector(".product__info-container");
        if (!target) return;

        target.appendChild(fullDetailsBtnTemplate.content.cloneNode(true));
      }

      reorderElements() {
        // The buy-buttons block renders pinned below the scrollable area.
        if (!this.footerContent) return;

        const buyButtons =
          this.modalContent.querySelector(".product__buy-buttons");
        if (buyButtons) this.footerContent.appendChild(buyButtons);
      }

      preventScrollBody() {
        // iOS Safari scrolls the page behind the fixed modal when a clipped
        // option radio receives focus (body overflow lock is ignored there).
        // Delegated focusin survives variant-change re-renders of the options;
        // called once from the constructor.
        this.modalContent.addEventListener("focusin", (event) => {
          if (
            !event.target.matches(
              '.product-form__controls-group input[type="radio"]'
            )
          ) {
            return;
          }
          const scrollTop =
            document.body.scrollTop || document.documentElement.scrollTop;
          setTimeout(() => {
            window.scrollTo(0, scrollTop);
          }, 0);
        });
      }

      preventVariantURLSwitching() {
        const variantPickerEl =
          this.modalContent.querySelectorAll("variant-picker");

        variantPickerEl.forEach((el) => {
          el.setAttribute("data-update-url", "false");
        });
      }

      clearMediaClasses() {
        // In this method, all classes are removed from product__media elements in order to initialize a forced media gallery type in the Quick view modal, independent of the product page.

        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        mediaWrapper.className = filteredClasses.join(" ");
      }

      forceSliderLayout() {
        // The modal gallery is ALWAYS a plain slider, whatever media layout the
        // Main product section is set to (grid / stacked / slider previews).
        // Runs on every initSlider call: the variant-change fetch can re-render
        // the gallery markup with the original layout attributes.
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        mediaWrapper.dataset.desktopType = "slider";
        mediaWrapper.dataset.mobileType = "slider";

        const mediaList = mediaWrapper.querySelector(".product__media-list");
        if (mediaList) {
          mediaList.dataset.desktopType = "slider";
          mediaList.dataset.mobileType = "slider";
          mediaList.classList.add("js-media-list");
        }

        mediaWrapper
          .querySelectorAll(".product__media-sublist-wrapper")
          .forEach((el) => el.remove());
      }

      destroySliders() {
        this._mainSwiper?.destroy(true, true);
        this._mainSwiper = null;
      }

      initSlider() {
        this.destroySliders();
        this.forceSliderLayout();

        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        const buttonPrev = sliderEl.querySelector(".slider-nav-button--prev");
        const buttonNext = sliderEl.querySelector(".slider-nav-button--next");
        const paginationEl = sliderEl.querySelector(".slider-pagination");
        if (!sliderWrapper || slides.length < 2) {
          if (buttonPrev) buttonPrev.remove();
          if (buttonNext) buttonNext.remove();
          if (paginationEl) paginationEl.remove();
          return;
        }

        sliderEl.classList.add("swiper");
        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        // Compact variant of the shared nav control for the in-media arrows:
        // a plain white pill without the secondary outline.
        [buttonPrev, buttonNext].forEach((btn) => {
          btn?.classList.add("slider-nav-button--compact");
          btn?.classList.remove("slider-nav-button--secondary");
        });

        const slideCount = slides.length;
        const isLoop = slideCount > 1 && slideCount <= 32;
        const speed = slideCount > 32 ? 0 : 800;

        this._mainSwiper = new Swiper(sliderEl, {
          slidesPerView: 1,
          spaceBetween: 10,
          autoHeight: false,
          loop: isLoop,
          direction: "horizontal",
          speed: speed,
          allowTouchMove: true,
          watchSlideProgress: true,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            nextEl: buttonNext,
            prevEl: buttonPrev,
            disabledClass: "swiper-button-disabled",
          },
          pagination: {
            el: paginationEl,
            clickable: true,
          },
          on: {
            slideChange: function () {
              window.pauseAllMedia(sliderEl);
              this.params.noSwiping = false;
            },
            slideChangeTransitionEnd: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              const posterBtn3D = this.slides[activeIndex]?.querySelector(
                ".shopify-model-viewer-ui__button--poster"
              );
              if (model3D && posterBtn3D) {
                posterBtn3D.removeAttribute("hidden");
              }
            },
            touchStart: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              if (model3D) {
                if (
                  !model3D.classList.contains(
                    "shopify-model-viewer-ui__disabled"
                  )
                ) {
                  this.params.noSwiping = true;
                  this.params.noSwipingClass = "swiper-slide";
                } else {
                  this.params.noSwiping = false;
                }
              }
            },
          },
        });
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__media-link"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }
    }
  );
}
